<template>
  <div class="ai-trip-page">
    <AppHeader />

    <main class="consult-shell">
      <router-link to="/mypage" class="back-link">← 旅行一覧へ戻る</router-link>

      <div class="hero">
        <div>
          <span class="hero-label">AI TRIP PLANNER</span>
          <h1>AI旅行相談</h1>
          <p>
            旅の条件を教えてください。AIの5つの提案から好みを選び、
            完成した旅程に納得してからしおりを作成できます。
          </p>
        </div>
        <div class="hero-icon" aria-hidden="true">
          <IconSvg name="plane-solid-full" size="xl" />
        </div>
      </div>

      <nav class="consult-progress" aria-label="AI旅行相談の進捗">
        <ol>
          <li
            v-for="step in consultationSteps"
            :key="step.number"
            :class="{
              active: currentStep === step.number,
              completed: currentStep > step.number,
            }"
          >
            <span>{{ step.number }}</span>
            <strong>{{ step.label }}</strong>
          </li>
        </ol>
      </nav>

      <form
        v-if="phase === 'conditions'"
        class="consult-grid"
        @submit.prevent="submit"
      >
        <div class="form-panel">
          <div class="form-phase-heading">
            <span class="hero-label result-label">STEP 1 / 4</span>
            <h2>旅行条件を入力してください</h2>
            <p>入力後、全体の流れ、食事・ホテル、最終旅程の順に選びます。</p>
          </div>
          <section class="form-section">
            <div class="section-heading">
              <span class="step-number">1</span>
              <div>
                <h2>どこへ行く？</h2>
                <p>エリアや都市名を入力してください</p>
              </div>
            </div>

            <label class="field-label" for="destination">行き先</label>
            <input
              id="destination"
              v-model.trim="form.destination"
              class="text-input"
              type="text"
              maxlength="120"
              placeholder="例：函館、箱根、台湾"
              required
            />

            <div class="choice-row compact">
              <button
                v-for="option in destinationTypes"
                :key="option.value"
                type="button"
                class="choice-card"
                :class="{ selected: form.destinationType === option.value }"
                @click="form.destinationType = option.value"
              >
                <span class="choice-symbol">{{ option.symbol }}</span>
                <span>{{ option.label }}</span>
              </button>
            </div>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <span class="step-number">2</span>
              <div>
                <h2>誰と、どうやって？</h2>
                <p>旅のメンバーと主な移動手段を選択</p>
              </div>
            </div>

            <span class="field-label">誰と旅をする？</span>
            <div class="choice-row">
              <button
                v-for="option in companionOptions"
                :key="option.value"
                type="button"
                class="choice-card"
                :class="{ selected: form.companion === option.value }"
                @click="selectCompanion(option.value)"
              >
                <span class="choice-symbol">{{ option.symbol }}</span>
                <span>{{ option.label }}</span>
              </button>
            </div>

            <div class="two-columns">
              <label class="field-group">
                <span class="field-label">人数</span>
                <input
                  v-model.number="form.travelers"
                  class="text-input"
                  type="number"
                  min="1"
                  max="20"
                  :disabled="isTravelerCountFixed"
                  required
                />
                <span v-if="isTravelerCountFixed" class="field-help">
                  {{ companionLabel }}は{{ form.travelers }}名で固定されます
                </span>
              </label>
              <label class="field-group">
                <span class="field-label">年代</span>
                <select v-model="form.ageGroup" class="text-input" required>
                  <option
                    v-for="option in ageOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>

            <span class="field-label">移動手段</span>
            <div class="choice-row compact">
              <button
                v-for="option in transportOptions"
                :key="option.value"
                type="button"
                class="choice-card"
                :class="{ selected: form.transport === option.value }"
                @click="form.transport = option.value"
              >
                <span class="choice-symbol">{{ option.symbol }}</span>
                <span>{{ option.label }}</span>
              </button>
            </div>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <span class="step-number">3</span>
              <div>
                <h2>いつ行く？</h2>
                <p>最大14日間の旅程を作成できます</p>
              </div>
            </div>

            <div class="two-columns">
              <label class="field-group">
                <span class="field-label">出発日</span>
                <input
                  v-model="form.startDate"
                  class="text-input"
                  type="date"
                  :min="minDate"
                  required
                />
              </label>
              <label class="field-group">
                <span class="field-label">帰着日</span>
                <input
                  v-model="form.endDate"
                  class="text-input"
                  type="date"
                  :min="form.startDate || minDate"
                  required
                />
              </label>
            </div>

            <div class="two-columns">
              <label class="field-group">
                <span class="field-label">開始時間帯</span>
                <select
                  v-model="form.startTimeSlot"
                  class="text-input"
                  required
                >
                  <option
                    v-for="option in timeSlotOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="field-group">
                <span class="field-label">終了時間帯</span>
                <select
                  v-model="form.endTimeSlot"
                  class="text-input"
                  required
                >
                  <option
                    v-for="option in timeSlotOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <span class="step-number">4</span>
              <div>
                <h2>旅の好みを教えて</h2>
                <p>出発エリアや興味を加えると提案が具体的になります</p>
              </div>
            </div>

            <div class="two-columns">
              <label class="field-group">
                <span class="field-label">拠点の出発地</span>
                <input
                  v-model.trim="form.baseDepartureArea"
                  class="text-input"
                  type="text"
                  maxlength="120"
                  placeholder="例：東京駅、新宿、自宅最寄り"
                  required
                />
              </label>
              <label class="field-group">
                <span class="field-label">現地の出発エリア</span>
                <input
                  v-model.trim="form.departureArea"
                  class="text-input"
                  type="text"
                  maxlength="120"
                  placeholder="例：函館駅"
                />
              </label>
              <label class="field-group">
                <span class="field-label">現地の最終エリア</span>
                <input
                  v-model.trim="form.finalArea"
                  class="text-input"
                  type="text"
                  maxlength="120"
                  placeholder="例：函館空港"
                />
              </label>
            </div>

            <span class="field-label">興味のあること（複数選択可）</span>
            <div class="interest-list">
              <button
                v-for="interest in interestOptions"
                :key="interest"
                type="button"
                class="interest-chip"
                :class="{ selected: form.interests.includes(interest) }"
                @click="toggleInterest(interest)"
              >
                {{ interest }}
              </button>
            </div>

            <label class="field-group budget-field">
              <span class="field-label">1人あたりの予算（円・任意）</span>
              <input
                v-model.number="form.budgetPerPerson"
                class="text-input"
                type="number"
                min="0"
                max="100000000"
                step="1000"
                placeholder="例：50000"
              />
              <span class="budget-total-preview">
                全体予算：{{ totalBudgetLabel }}
              </span>
            </label>

            <label class="field-group">
              <span class="field-label">その他の希望（任意）</span>
              <textarea
                v-model.trim="form.notes"
                class="text-input textarea"
                maxlength="1000"
                rows="4"
                placeholder="例：雨でも楽しめる場所を含めたい、海鮮を食べたい"
              ></textarea>
            </label>
          </section>
        </div>

        <aside class="submit-panel">
          <div class="assistant-card">
            <div class="assistant-avatar">
              <IconSvg name="map-solid-full" size="lg" />
            </div>
            <p class="assistant-name">RabiiTrip AI</p>
            <h2>あなた向けの旅程を作ります</h2>
            <p>
              入力した条件から、移動や食事を含む日ごとの予定を組み立てます。
            </p>

            <dl class="condition-summary">
              <div>
                <dt>行き先</dt>
                <dd>{{ form.destination || "未入力" }}</dd>
              </div>
              <div>
                <dt>日程</dt>
                <dd>{{ tripDateSummary }}</dd>
              </div>
              <div>
                <dt>スタイル</dt>
                <dd>{{ companionLabel }}・{{ transportLabel }}</dd>
              </div>
            </dl>

            <p v-if="localError || aiTripStore.error" class="error-message">
              {{ localError || aiTripStore.error }}
            </p>

            <div class="generation-action-row">
              <button
                class="generate-button"
                type="submit"
                :disabled="aiTripStore.isGenerating"
              >
                <span v-if="aiTripStore.isGenerating" class="spinner"></span>
                {{
                  aiTripStore.isGenerating
                    ? "AIが5つの候補を考えています..."
                    : "この条件で候補を見る"
                }}
              </button>
              <button
                v-if="aiTripStore.isGenerating"
                class="cancel-generation-button"
                type="button"
                aria-label="AI相談を停止"
                title="AI相談を停止"
                @click="stopGeneration"
              >
                ■
              </button>
            </div>
            <p class="submit-note">
              この段階ではしおりへ保存されません。
            </p>
          </div>
        </aside>
      </form>

      <section v-else-if="phase === 'choices'" class="result-panel">
        <div class="result-heading">
          <div>
            <span class="hero-label result-label">STEP 2</span>
            <h2>旅行全体の流れを選んでください</h2>
            <p>{{ aiTripStore.proposals?.message }}</p>
          </div>
          <button class="secondary-button" type="button" @click="backToConditions">
            条件を変更
          </button>
        </div>
        <div class="result-actions result-actions-top">
          <button
            class="generate-button result-primary-button"
            type="button"
            :disabled="!selections.flowOption || aiTripStore.isGenerating"
            @click="createDailyOptions"
          >
            <span v-if="aiTripStore.isGenerating" class="spinner"></span>
            {{
              aiTripStore.isGenerating
                ? "昼食・夕食・ホテル候補を作成中..."
                : "この流れで食事・ホテルを選ぶ"
            }}
          </button>
        </div>

        <div class="proposal-group">
          <div class="proposal-group-heading">
            <span class="step-number">A</span>
            <div>
              <h3>旅行全体の流れ</h3>
              <p>観光の順序、ペース、旅のテーマが異なる5案です</p>
            </div>
          </div>
          <div class="proposal-grid">
            <button
              v-for="option in aiTripStore.proposals?.flowOptions || []"
              :key="option.id"
              type="button"
              class="proposal-card"
              :class="{ selected: selections.flowOption?.id === option.id }"
              @click="selections.flowOption = option"
            >
              <span class="proposal-select-mark">
                {{ selections.flowOption?.id === option.id ? "選択中" : "選ぶ" }}
              </span>
              <strong>{{ option.title }}</strong>
              <p>{{ option.description }}</p>
              <ul>
                <li v-for="highlight in option.highlights" :key="highlight">
                  {{ highlight }}
                </li>
              </ul>
              <span v-if="option.estimatedCost" class="proposal-cost">
                <span>1人あたり {{ costPerPersonLabel(option, "total") }}</span>
                <span>全体 {{ costTotalLabel(option, "total") }}</span>
              </span>
            </button>
          </div>
        </div>

        <p v-if="localError || aiTripStore.error" class="error-message">
          {{ localError || aiTripStore.error }}
        </p>
        <div class="result-actions">
          <button
            class="generate-button result-primary-button"
            type="button"
            :disabled="!selections.flowOption || aiTripStore.isGenerating"
            @click="createDailyOptions"
          >
            <span v-if="aiTripStore.isGenerating" class="spinner"></span>
            {{
              aiTripStore.isGenerating
                ? "昼食・夕食・ホテル候補を作成中..."
                : "この流れで食事・ホテルを選ぶ"
              }}
          </button>
          <button
            v-if="aiTripStore.isGenerating"
            class="cancel-generation-button inline-cancel-button"
            type="button"
            aria-label="AI相談を停止"
            title="AI相談を停止"
            @click="stopGeneration"
          >
            ■
          </button>
        </div>
      </section>

      <section v-else-if="phase === 'daily'" class="result-panel">
        <div class="result-heading">
          <div>
            <span class="hero-label result-label">STEP 3</span>
            <h2>昼食・夕食・ホテル候補を確認してください</h2>
            <p>{{ aiTripStore.dailyOptions?.message }}</p>
          </div>
          <button class="secondary-button" type="button" @click="phase = 'choices'">
            全体の流れを選び直す
          </button>
        </div>
        <div class="result-actions result-actions-top">
          <button
            class="generate-button result-primary-button"
            type="button"
            :disabled="!allDailyChoicesSelected || aiTripStore.isGenerating"
            @click="createPreview"
          >
            <span v-if="aiTripStore.isGenerating" class="spinner"></span>
            {{
              aiTripStore.isGenerating
                ? "選択内容から旅程を作成中..."
                : "旅程を見る"
            }}
          </button>
        </div>

        <div class="flow-selection-banner">
          <span>選択した全体の流れ</span>
          <strong>{{ selections.flowOption?.title }}</strong>
        </div>

        <div class="regeneration-toolbar">
          <div>
            <strong>気に入らない候補をまとめて再提案</strong>
            <p>
              昼食・夕食・ホテルの「再提案する」を複数選び、まとめて実行できます。
            </p>
          </div>
          <div class="regeneration-actions">
            <span>{{ regenerationTargets.length }}件選択中</span>
            <button
              class="secondary-button regenerate-button"
              type="button"
              :disabled="
                regenerationTargets.length === 0 || aiTripStore.isGenerating
              "
              @click="regenerateSelectedOptions"
            >
              {{
                aiTripStore.generationTask === "regenerate-options"
                  ? "新しい候補を作成中..."
                  : "選択した候補をまとめて再提案"
              }}
            </button>
            <button
              v-if="aiTripStore.generationTask === 'regenerate-options'"
              class="cancel-generation-button inline-cancel-button"
              type="button"
              aria-label="AI相談を停止"
              title="AI相談を停止"
              @click="stopGeneration"
            >
              ■
            </button>
          </div>
        </div>

        <article
          v-for="day in aiTripStore.dailyOptions?.days || []"
          :key="day.date"
          class="daily-choice-day"
        >
          <h3>{{ day.date }}</h3>
          <div class="day-schedule-preview">
            <div class="daily-choice-heading">
              <strong>この日の全体スケジュール</strong>
              <span>食事・ホテルを選ぶ前の予定です</span>
            </div>
            <div
              v-for="item in day.scheduleItems"
              :key="`${day.date}-${item.startTime}-${item.title}`"
              class="schedule-preview-item"
            >
              <div class="itinerary-time">
                {{ item.startTime || "--:--" }}
                <span v-if="item.endTime">〜 {{ item.endTime }}</span>
              </div>
              <div class="itinerary-content">
                <span class="category-pill">{{
                  categoryLabel(item.category)
                }}</span>
                <strong>{{ item.title }}</strong>
                <p v-if="item.remarks">{{ item.remarks }}</p>
              </div>
              <span v-if="item.cost" class="itinerary-cost">
                ¥{{ Number(item.cost).toLocaleString() }}
              </span>
            </div>
          </div>
          <div
            v-for="group in dailyChoiceGroups(day)"
            :key="`${day.date}-${group.key}`"
            class="daily-choice-group"
          >
            <div class="daily-choice-heading">
              <div>
                <strong>{{ group.title }}</strong>
                <span>採用したい候補があれば選択（未選択でも作成可）</span>
              </div>
              <label class="regeneration-checkbox">
                <input
                  type="checkbox"
                  :checked="isRegenerationTarget(day.date, group.key)"
                  :disabled="aiTripStore.isGenerating"
                  @change="toggleRegenerationTarget(day.date, group.key)"
                />
                この枠を再提案
              </label>
            </div>
            <div class="proposal-grid">
              <div
                v-for="option in group.options"
                :key="option.id"
                class="proposal-card daily-proposal-card"
                :class="{
                  selected:
                    getDailySelection(day.date, group.key)?.id === option.id,
                }"
                role="button"
                tabindex="0"
                @click="selectDailyOption(day.date, group.key, option)"
                @keydown.enter.prevent="
                  selectDailyOption(day.date, group.key, option)
                "
                @keydown.space.prevent="
                  selectDailyOption(day.date, group.key, option)
                "
              >
                <span class="proposal-select-mark">
                  {{
                    getDailySelection(day.date, group.key)?.id === option.id
                      ? "選択中"
                      : "選ぶ"
                  }}
                </span>
                <strong>{{ option.title }}</strong>
                <a
                  v-if="option.googleMapsUrl"
                  class="google-maps-link"
                  :href="option.googleMapsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >
                  Googleマップで見る
                </a>
                <p>{{ option.description }}</p>
                <ul>
                  <li v-for="highlight in option.highlights" :key="highlight">
                    {{ highlight }}
                  </li>
                </ul>
                <span v-if="option.estimatedCost" class="proposal-cost">
                  <span>1人あたり {{ costPerPersonLabel(option, "perPerson") }}</span>
                  <span>全体 {{ costTotalLabel(option, "perPerson") }}</span>
                </span>
              </div>
            </div>
          </div>
        </article>

        <label class="keep-candidates-toggle">
          <input v-model="keepOtherCandidates" type="checkbox" />
          <span>ほかの候補も行きたいところリストに残す</span>
        </label>

        <p v-if="localError || aiTripStore.error" class="error-message">
          {{ localError || aiTripStore.error }}
        </p>
        <div class="result-actions">
          <button
            class="generate-button result-primary-button"
            type="button"
            :disabled="!allDailyChoicesSelected || aiTripStore.isGenerating"
            @click="createPreview"
          >
            <span v-if="aiTripStore.isGenerating" class="spinner"></span>
            {{
              aiTripStore.isGenerating
                ? "選択内容から旅程を作成中..."
                : "確認した内容で旅程を見る"
            }}
          </button>
          <button
            v-if="aiTripStore.generationTask === 'preview'"
            class="cancel-generation-button inline-cancel-button"
            type="button"
            aria-label="AI相談を停止"
            title="AI相談を停止"
            @click="stopGeneration"
          >
            ■
          </button>
        </div>
      </section>

      <section v-else class="result-panel">
        <div class="result-heading">
          <div>
            <span class="hero-label result-label">STEP 4</span>
            <h2>{{ aiTripStore.preview?.title }}</h2>
            <p>{{ aiTripStore.preview?.summary }}</p>
          </div>
          <button class="secondary-button" type="button" @click="phase = 'daily'">
            食事・ホテルを選び直す
          </button>
        </div>

        <div class="flow-selection-banner">
          <span>選択した全体の流れ</span>
          <strong>{{ selections.flowOption?.title }}</strong>
        </div>

        <div class="itinerary-days">
          <article
            v-for="day in aiTripStore.preview?.days || []"
            :key="day.date"
            class="itinerary-day"
          >
            <h3>{{ day.date }}</h3>
            <div
              v-for="item in day.items"
              :key="`${day.date}-${item.startTime}-${item.title}`"
              class="itinerary-item"
            >
              <div class="itinerary-time">
                {{ item.startTime || "--:--" }}
                <span v-if="item.endTime">〜 {{ item.endTime }}</span>
              </div>
              <div class="itinerary-content">
                <span class="category-pill">{{ categoryLabel(item.category) }}</span>
                <strong>{{ item.title }}</strong>
                <p v-if="item.remarks">{{ item.remarks }}</p>
                <p v-if="item.address">{{ item.address }}</p>
              </div>
              <span v-if="item.cost" class="itinerary-cost">
                ¥{{ Number(item.cost).toLocaleString() }}
              </span>
            </div>
          </article>
        </div>

        <p v-if="localError || aiTripStore.error" class="error-message">
          {{ localError || aiTripStore.error }}
        </p>
        <div v-if="savedShioriId" class="approval-box saved-trip-recovery" role="status">
          <div>
            <strong>しおりは保存済みです</strong>
            <p>{{ postSaveWarning || "作成済みのしおりを開いて確認できます。" }}</p>
          </div>
          <div class="saved-trip-actions">
            <router-link class="generate-button" :to="`/shiori/${savedShioriId}`">作成済みの旅程を開く</router-link>
            <router-link class="back-link" to="/mypage">マイページへ戻る</router-link>
          </div>
        </div>
        <div v-else class="approval-box">
          <div>
            <strong>この内容でよろしいですか？</strong>
            <p>「しおりを作成」を押すまでデータベースには保存されません。</p>
          </div>
          <button
            class="generate-button approval-button"
            type="button"
            :disabled="aiTripStore.isSaving"
            @click="saveApprovedTrip"
          >
            <span v-if="aiTripStore.isSaving" class="spinner"></span>
            {{ aiTripStore.isSaving ? "保存中..." : "この内容でしおりを作成" }}
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { computed, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import IconSvg from "@/components/IconSvg.vue";
import {
  cancelAiTripGeneration,
  useAiTripStore,
} from "@/store/ai-trip-store.js";
import { useShioriStore } from "@/store/shiori-store.js";
import {
  beginShioriDestinationImageWorkflow,
  ensureShioriDestinationImage,
  isShioriDestinationImageWorkflowCurrent,
} from "@/utils/ensure-shiori-destination-image.js";

export default {
  name: "AiTripConsultView",
  components: {
    AppHeader,
    IconSvg,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const aiTripStore = useAiTripStore();
    const shioriStore = useShioriStore();
    const localError = ref("");
    const savedShioriId = ref("");
    const postSaveWarning = ref("");
    const phase = ref("conditions");
    const regenerationTargets = ref([]);
    const keepOtherCandidates = ref(false);
    const today = new Date();
    const minDate = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, "0"),
      String(today.getDate()).padStart(2, "0"),
    ].join("-");

    const destinationTypes = [
      { value: "domestic", label: "国内", symbol: "JP" },
      { value: "international", label: "海外", symbol: "GL" },
    ];
    const companionOptions = [
      { value: "family", label: "家族", symbol: "家" },
      { value: "couple", label: "カップル", symbol: "2人" },
      { value: "friends", label: "友人", symbol: "仲間" },
      { value: "solo", label: "一人旅", symbol: "1人" },
    ];
    const transportOptions = [
      { value: "car", label: "車", symbol: "CAR" },
      { value: "public_transport", label: "公共交通", symbol: "TRAIN" },
    ];
    const ageOptions = [
      { value: "under_10", label: "10歳未満" },
      { value: "teens", label: "10代" },
      { value: "20s", label: "20代" },
      { value: "30s", label: "30代" },
      { value: "40s", label: "40代" },
      { value: "50s", label: "50代" },
      { value: "60s", label: "60代" },
      { value: "70_plus", label: "70代以上" },
    ];
    const timeSlotOptions = [
      { value: "morning", label: "午前" },
      { value: "lunch", label: "昼食ごろ" },
      { value: "afternoon", label: "午後" },
      { value: "evening", label: "夕方" },
      { value: "dinner", label: "夕食ごろ" },
    ];
    const interestOptions = [
      "グルメ",
      "温泉",
      "絶景",
      "歴史・文化",
      "自然",
      "ショッピング",
      "アクティビティ",
      "子ども向け",
    ];
    const consultationSteps = [
      { number: 1, label: "条件入力" },
      { number: 2, label: "全体の流れ" },
      { number: 3, label: "食事・ホテル" },
      { number: 4, label: "最終確認" },
    ];

    const form = reactive({
      destination: "",
      destinationType: "domestic",
      companion: "family",
      travelers: 2,
      transport: "public_transport",
      ageGroup: "30s",
      startDate: "",
      endDate: "",
      startTimeSlot: "morning",
      endTimeSlot: "evening",
      baseDepartureArea: "",
      departureArea: "",
      finalArea: "",
      interests: [],
      budgetPerPerson: null,
      notes: "",
    });

    const addDays = (date, days) => {
      const next = new Date(date);
      next.setDate(next.getDate() + days);
      return [
        next.getFullYear(),
        String(next.getMonth() + 1).padStart(2, "0"),
        String(next.getDate()).padStart(2, "0"),
      ].join("-");
    };

    const applyQueryPrefill = () => {
      const query = route.query || {};
      const destination = typeof query.destination === "string"
        ? query.destination.trim()
        : "";
      if (destination) form.destination = destination;
      const positiveNumber = (value) => {
        if (Array.isArray(value) || value === null || value === undefined || value === "") return null;
        const number = Number(value);
        return Number.isFinite(number) && number > 0 ? number : null;
      };
      const travelers = positiveNumber(query.travelers);
      if (travelers) form.travelers = Math.min(Math.max(Math.floor(travelers), 1), 20);
      const budgetPerPerson = positiveNumber(query.budgetPerPerson ?? query.budget);
      if (budgetPerPerson) form.budgetPerPerson = budgetPerPerson;
      const days = positiveNumber(query.days);
      if (days && !form.startDate && !form.endDate) {
        form.startDate = minDate;
        form.endDate = addDays(today, Math.max(Math.floor(days) - 1, 0));
      }
    };

    applyQueryPrefill();
    const selections = reactive({
      flowOption: null,
      dailySelections: {},
    });
    const currentStep = computed(
      () =>
        ({
          conditions: 1,
          choices: 2,
          daily: 3,
          preview: 4,
        })[phase.value] || 1,
    );

    const companionLabel = computed(
      () =>
        companionOptions.find((option) => option.value === form.companion)
          ?.label || "",
    );
    const isTravelerCountFixed = computed(() =>
      ["couple", "solo"].includes(form.companion),
    );
    const transportLabel = computed(
      () =>
        transportOptions.find((option) => option.value === form.transport)
          ?.label || "",
    );
    const tripDateSummary = computed(() => {
      if (!form.startDate || !form.endDate) return "未入力";
      return `${form.startDate} 〜 ${form.endDate}`;
    });
    const totalBudget = computed(() => {
      const perPerson = Number(form.budgetPerPerson || 0);
      if (!perPerson) return null;
      return perPerson * Number(form.travelers || 1);
    });
    const totalBudgetLabel = computed(() =>
      totalBudget.value ? `¥${totalBudget.value.toLocaleString()}` : "-",
    );
    const allDailyChoicesSelected = computed(
      () => (aiTripStore.dailyOptions?.days || []).length > 0,
    );

    const formatCurrency = (value) => {
      const amount = Number(value || 0);
      if (!amount) return "-";
      return `¥${Math.round(amount).toLocaleString()}`;
    };

    const costAmount = (option, basis) => {
      const amount = Number(option?.estimatedCost || 0);
      const travelers = Math.max(Number(form.travelers || 1), 1);
      if (!amount) {
        return { perPerson: 0, total: 0 };
      }
      if (basis === "total") {
        return {
          perPerson: amount / travelers,
          total: amount,
        };
      }
      return {
        perPerson: amount,
        total: amount * travelers,
      };
    };

    const costPerPersonLabel = (option, basis) =>
      formatCurrency(costAmount(option, basis).perPerson);

    const costTotalLabel = (option, basis) =>
      formatCurrency(costAmount(option, basis).total);

    const toggleInterest = (interest) => {
      const index = form.interests.indexOf(interest);
      if (index >= 0) {
        form.interests.splice(index, 1);
      } else if (form.interests.length < 8) {
        form.interests.push(interest);
      }
    };

    const selectCompanion = (companion) => {
      form.companion = companion;
      if (companion === "couple") {
        form.travelers = 2;
      } else if (companion === "solo") {
        form.travelers = 1;
      }
    };

    const buildConditions = () => ({
      destination: form.destination,
      destinationType: form.destinationType,
      companion: form.companion,
      travelers: form.travelers,
      transport: form.transport,
      ageGroup: form.ageGroup,
      startDate: form.startDate,
      endDate: form.endDate,
      startTimeSlot: form.startTimeSlot,
      endTimeSlot: form.endTimeSlot,
      baseDepartureArea: form.baseDepartureArea || null,
      departureArea: form.departureArea || null,
      finalArea: form.finalArea || null,
      interests: [...form.interests],
      budget: totalBudget.value,
      notes: form.notes || null,
    });

    const validateDates = () => {
      if (form.endDate < form.startDate) {
        localError.value = "帰着日は出発日以降を選択してください。";
        return false;
      }

      const dayCount =
        (new Date(form.endDate) - new Date(form.startDate)) / 86400000 + 1;
      if (dayCount > 14) {
        localError.value = "AI旅行相談で作成できる旅程は14日間までです。";
        return false;
      }
      return true;
    };

    const submit = async () => {
      localError.value = "";
      if (!validateDates()) return;
      try {
        await aiTripStore.generateProposals(buildConditions());
        selections.flowOption = null;
        selections.dailySelections = {};
        phase.value = "choices";
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        if (error.code !== "ERR_CANCELED") {
          console.error("AI旅行候補の生成エラー:", error);
        }
      }
    };

    const createDailyOptions = async () => {
      localError.value = "";
      if (!selections.flowOption) {
        localError.value = "旅行全体の流れを1つ選択してください。";
        return;
      }
      try {
        await aiTripStore.generateDailyOptions(
          buildConditions(),
          selections.flowOption,
        );
        selections.dailySelections = {};
        phase.value = "daily";
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        if (error.code !== "ERR_CANCELED") {
          console.error("AI食事・ホテル候補の生成エラー:", error);
        }
      }
    };

    const dailyChoiceGroups = (day) => {
      const groups = [
        {
          key: "lunchOption",
          title: "昼食",
          options: day.lunchOptions,
        },
        {
          key: "dinnerOption",
          title: "夕食",
          options: day.dinnerOptions,
        },
      ];
      if (day.hotelOptions.length) {
        groups.push({
          key: "hotelOption",
          title: "ホテル",
          options: day.hotelOptions,
        });
      }
      return groups;
    };

    const getDailySelection = (date, key) =>
      selections.dailySelections[date]?.[key] || null;

    const selectDailyOption = (date, key, option) => {
      if (!selections.dailySelections[date]) {
        selections.dailySelections[date] = {};
      }
      selections.dailySelections[date][key] = option;
    };

    const regenerationTargetId = (date, key) => `${date}:${key}`;

    const isRegenerationTarget = (date, key) =>
      regenerationTargets.value.includes(regenerationTargetId(date, key));

    const toggleRegenerationTarget = (date, key) => {
      const targetId = regenerationTargetId(date, key);
      if (regenerationTargets.value.includes(targetId)) {
        regenerationTargets.value = regenerationTargets.value.filter(
          (item) => item !== targetId,
        );
      } else {
        regenerationTargets.value.push(targetId);
      }
    };

    const regenerateSelectedOptions = async () => {
      localError.value = "";
      const targets = [];
      (aiTripStore.dailyOptions?.days || []).forEach((day) => {
        dailyChoiceGroups(day).forEach((group) => {
          if (isRegenerationTarget(day.date, group.key)) {
            targets.push({
              date: day.date,
              optionType: group.key,
              previousOptions: group.options,
            });
          }
        });
      });
      if (!targets.length) {
        localError.value = "再提案する昼食・夕食・ホテルを選択してください。";
        return;
      }

      try {
        await aiTripStore.regenerateDailyOptions(
          buildConditions(),
          selections.flowOption,
          targets,
        );
        targets.forEach((target) => {
          if (selections.dailySelections[target.date]) {
            delete selections.dailySelections[target.date][target.optionType];
          }
        });
        regenerationTargets.value = [];
      } catch (error) {
        if (error.code !== "ERR_CANCELED") {
          console.error("AI候補の一括再提案エラー:", error);
        }
      }
    };

    const buildAutoOption = (date, key) => ({
      id: `auto-${date}-${key}`,
      title: "AIに任せる",
      description: "この候補は選択せず、詳細な旅程作成時にAIへ最適化を任せます。",
      highlights: ["未選択", "AIが旅程全体に合わせて調整"],
      estimatedCost: null,
      googleMapsUrl: null,
    });

    const stopGeneration = () => {
      cancelAiTripGeneration();
      localError.value = "";
    };

    const createPreview = async () => {
      localError.value = "";
      if (!allDailyChoicesSelected.value) {
        localError.value =
          "昼食・夕食・ホテル候補の作成が完了してから旅程を作成してください。";
        return;
      }
      try {
        const dailySelections = (aiTripStore.dailyOptions?.days || []).map(
          (day) => ({
            date: day.date,
            scheduleItems: day.scheduleItems,
            lunchOption:
              selections.dailySelections[day.date]?.lunchOption ||
              buildAutoOption(day.date, "lunch"),
            dinnerOption:
              selections.dailySelections[day.date]?.dinnerOption ||
              buildAutoOption(day.date, "dinner"),
            hotelOption:
              selections.dailySelections[day.date]?.hotelOption ||
              (day.hotelOptions.length
                ? buildAutoOption(day.date, "hotel")
                : null),
          }),
        );
        await aiTripStore.generatePreview(buildConditions(), {
          flowOption: selections.flowOption,
          dailySelections,
        });
        phase.value = "preview";
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        if (error.code !== "ERR_CANCELED") {
          console.error("AI詳細旅程の生成エラー:", error);
        }
      }
    };

    const buildPreservedOptions = () => {
      if (!keepOtherCandidates.value) return [];
      const preserved = new Map();
      (aiTripStore.dailyOptions?.days || []).forEach((day) => {
        dailyChoiceGroups(day).forEach((group) => {
          const selectedId = getDailySelection(day.date, group.key)?.id;
          group.options.forEach((option) => {
            if (option.id === selectedId) return;
            preserved.set(`${group.key}:${option.id}`, option);
          });
        });
      });
      return Array.from(preserved.values());
    };

    const saveApprovedTrip = async () => {
      if (savedShioriId.value || aiTripStore.isSaving) return;
      localError.value = "";
      const imageWorkflowSession = beginShioriDestinationImageWorkflow();
      let result;
      try {
        result = await aiTripStore.saveTrip(
          buildConditions(),
          aiTripStore.preview,
          buildPreservedOptions(),
        );
        savedShioriId.value = result?.shiori_id || result?.shioriId || "";
        if (!savedShioriId.value) {
          localError.value = "しおりは保存されました。マイページから作成済み旅程を確認してください。";
          return;
        }
      } catch (error) {
        console.error("AI旅程の保存エラー:", error);
        return;
      }
      try {
        if (!isShioriDestinationImageWorkflowCurrent(imageWorkflowSession)) throw new Error("inactive image workflow");
        await ensureShioriDestinationImage({
          shioriId: savedShioriId.value,
          destinationName: form.destination,
          shioriStore,
          session: imageWorkflowSession,
        });
        if (!isShioriDestinationImageWorkflowCurrent(imageWorkflowSession)) return;
        await shioriStore.fetchShiori({ force: true });
        if (shioriStore.error) throw new Error("作成済みしおりの再読み込みに失敗しました。");
        if (!isShioriDestinationImageWorkflowCurrent(imageWorkflowSession)) return;
        await router.push(`/shiori/${savedShioriId.value}`);
      } catch (error) {
        console.error("保存後処理エラー:", error);
        postSaveWarning.value = "画面を更新できませんでした。下のリンクから作成済み旅程を開いてください。";
      }
    };

    const backToConditions = () => {
      phase.value = "conditions";
      regenerationTargets.value = [];
      aiTripStore.error = null;
      localError.value = "";
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const categoryLabel = (category) => {
      const labels = {
        hotel: "宿泊",
        restaurant: "食事",
        transport: "移動",
        sightseeing: "観光",
        shopping: "買い物",
        activity: "体験",
        other: "その他",
      };
      return labels[category] || "予定";
    };

    return {
      aiTripStore,
      localError,
      savedShioriId,
      postSaveWarning,
      phase,
      minDate,
      form,
      selections,
      keepOtherCandidates,
      consultationSteps,
      currentStep,
      regenerationTargets,
      destinationTypes,
      companionOptions,
      transportOptions,
      ageOptions,
      timeSlotOptions,
      interestOptions,
      companionLabel,
      isTravelerCountFixed,
      transportLabel,
      tripDateSummary,
      totalBudgetLabel,
      costPerPersonLabel,
      costTotalLabel,
      allDailyChoicesSelected,
      toggleInterest,
      selectCompanion,
      submit,
      createDailyOptions,
      dailyChoiceGroups,
      getDailySelection,
      selectDailyOption,
      isRegenerationTarget,
      toggleRegenerationTarget,
      regenerateSelectedOptions,
      stopGeneration,
      createPreview,
      saveApprovedTrip,
      backToConditions,
      categoryLabel,
    };
  },
};
</script>

<style scoped>
.ai-trip-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 10% 10%, rgba(180, 207, 158, 0.2), transparent 30%),
    var(--color-background);
}

.consult-shell {
  width: min(1180px, calc(100% - 2rem));
  margin: 0 auto;
  padding: 2rem 0 4rem;
}

.back-link {
  display: inline-flex;
  margin-bottom: 1.25rem;
  color: var(--primary-hover);
  font-weight: 700;
  text-decoration: none;
}

.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 2rem;
  margin-bottom: 1.5rem;
  color: white;
  background: linear-gradient(135deg, #b4cf9e 0%, #b4cf9e 65%, #d7e7ca 100%);
  border-radius: 24px;
  box-shadow: var(--shadow-lg);
}

.hero h1 {
  margin: 0.35rem 0 0.75rem;
  color: white;
}

.hero p {
  max-width: 680px;
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
}

.hero-label {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.hero-icon {
  display: grid;
  width: 92px;
  height: 92px;
  flex: 0 0 auto;
  place-items: center;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
}

.consult-progress {
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
}

.consult-progress ol {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.consult-progress li {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--color-text-light);
}

.consult-progress li > span {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  place-items: center;
  background: var(--color-cool-bg);
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 800;
}

.consult-progress li.active {
  color: var(--primary-hover);
}

.consult-progress li.active > span {
  color: white;
  background: var(--primary-hover);
}

.consult-progress li.completed > span {
  color: var(--primary-hover);
  background: var(--color-primary-light);
}

.consult-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 350px;
  gap: 1.5rem;
  align-items: start;
}

.form-panel,
.assistant-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
}

.form-phase-heading {
  padding: 1.5rem 1.75rem;
  background: var(--color-primary-light);
  border-bottom: 1px solid var(--color-border);
  border-radius: 20px 20px 0 0;
}

.form-phase-heading h2 {
  margin: 0.35rem 0;
  font-size: 1.3rem;
}

.form-phase-heading p {
  margin: 0;
  font-size: 0.85rem;
}

.form-section {
  padding: 1.75rem;
  border-bottom: 1px solid var(--color-border);
}

.form-section:last-child {
  border-bottom: 0;
}

.section-heading {
  display: flex;
  gap: 0.9rem;
  margin-bottom: 1.5rem;
}

.section-heading h2 {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
}

.section-heading p {
  margin: 0;
  font-size: 0.875rem;
}

.step-number {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  place-items: center;
  color: white;
  font-weight: 800;
  background: var(--primary-hover);
  border-radius: 50%;
}

.field-label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-text);
  font-size: 0.875rem;
  font-weight: 700;
}

.field-group {
  display: block;
}

.text-input {
  width: 100%;
  min-height: 46px;
  padding: 0.7rem 0.85rem;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  font: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.text-input:focus {
  border-color: var(--primary-hover);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.15);
  outline: none;
}

.text-input:disabled {
  color: var(--color-text-muted);
  background: var(--color-cool-bg);
  cursor: not-allowed;
}

.field-help {
  display: block;
  margin-top: 0.4rem;
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 700;
}

.textarea {
  resize: vertical;
}

.two-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin: 1.25rem 0;
}

.choice-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 1.25rem;
}

.choice-row.compact {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0.75rem 0 0;
}

.choice-card {
  display: flex;
  min-height: 66px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.65rem;
  color: var(--color-text-muted);
  background: var(--color-background);
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}

.choice-card.selected {
  color: var(--primary-hover);
  background: var(--color-primary-light);
  border-color: var(--primary-color);
}

.choice-symbol {
  font-size: 0.75rem;
  font-weight: 800;
}

.interest-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.interest-chip {
  padding: 0.55rem 0.9rem;
  color: var(--color-text-muted);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
}

.interest-chip.selected {
  color: white;
  background: var(--primary-hover);
  border-color: var(--primary-hover);
}

.budget-field {
  max-width: 360px;
  margin: 1.25rem 0;
}

.budget-total-preview {
  display: inline-flex;
  margin-top: 0.5rem;
  color: var(--primary-hover);
  font-size: 0.9rem;
  font-weight: 700;
}

.submit-panel {
  position: sticky;
  top: 1.5rem;
}

.assistant-card {
  padding: 1.5rem;
  text-align: center;
}

.assistant-avatar {
  display: grid;
  width: 64px;
  height: 64px;
  margin: 0 auto 0.75rem;
  place-items: center;
  color: white;
  background: linear-gradient(135deg, var(--primary-hover), var(--primary-color));
  border-radius: 18px;
}

.assistant-name {
  margin-bottom: 0.35rem;
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.assistant-card h2 {
  margin-bottom: 0.75rem;
  font-size: 1.25rem;
}

.condition-summary {
  margin: 1.25rem 0;
  padding: 1rem;
  text-align: left;
  background: var(--color-background);
  border-radius: 12px;
}

.condition-summary div {
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 0.5rem;
  padding: 0.35rem 0;
}

.condition-summary dt {
  color: var(--color-text-light);
  font-size: 0.8rem;
}

.condition-summary dd {
  overflow-wrap: anywhere;
  color: var(--color-text);
  font-size: 0.875rem;
  font-weight: 700;
}

.generate-button {
  display: flex;
  width: 100%;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.8rem;
  color: white;
  background: linear-gradient(135deg, #b4cf9e, #c6dab0);
  border: 0;
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  cursor: pointer;
  font: inherit;
  font-weight: 800;
}

.generate-button:disabled {
  cursor: wait;
  opacity: 0.75;
}

.generation-action-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.generation-action-row .generate-button {
  flex: 1;
}

.cancel-generation-button {
  width: 42px;
  height: 42px;
  min-height: 42px;
  flex: 0 0 42px;
  margin: 0;
  padding: 0;
  color: #5f6368;
  background: #eef0f2;
  border: 1px solid #c9cdd2;
  border-radius: 10px;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
}

.cancel-generation-button:hover {
  color: #3c4043;
  background: #dfe2e5;
}

.inline-cancel-button {
  width: 42px;
  min-width: 42px;
  margin-top: 0;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.error-message {
  padding: 0.75rem;
  color: #b42318;
  background: #fef3f2;
  border-radius: 10px;
  font-size: 0.85rem;
  text-align: left;
}

.submit-note {
  margin: 0.75rem 0 0;
  font-size: 0.75rem;
}

.result-panel {
  padding: 2rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
}

.result-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.result-heading h2 {
  margin: 0.35rem 0 0.5rem;
}

.result-heading p {
  margin: 0;
}

.result-label {
  color: var(--primary-hover);
}

.secondary-button {
  flex: 0 0 auto;
  padding: 0.65rem 1rem;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}

.proposal-group {
  padding: 1.75rem 0;
  border-bottom: 1px solid var(--color-border);
}

.proposal-group-heading {
  display: flex;
  gap: 0.8rem;
  margin-bottom: 1rem;
}

.proposal-group-heading h3,
.proposal-group-heading p {
  margin: 0;
}

.proposal-group-heading p {
  font-size: 0.85rem;
}

.proposal-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.proposal-card {
  position: relative;
  display: flex;
  min-height: 250px;
  flex-direction: column;
  align-items: flex-start;
  padding: 1.2rem;
  color: var(--color-text);
  text-align: left;
  background: var(--color-background);
  border: 2px solid transparent;
  border-radius: 14px;
  cursor: pointer;
  font: inherit;
  transition:
    transform 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;
}

.proposal-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.proposal-card.selected {
  background: var(--color-primary-light);
  border-color: var(--primary-hover);
  box-shadow: 0 8px 18px rgba(180, 207, 158, 0.18);
}

.proposal-card strong {
  padding-right: 3.5rem;
  font-size: 1.05rem;
}

.proposal-card p {
  margin: 0.65rem 0;
  font-size: 0.85rem;
}

.proposal-card ul {
  margin: 0;
  padding-left: 1.2rem;
  color: var(--color-text-muted);
  font-size: 0.82rem;
}

.proposal-select-mark {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  padding: 0.25rem 0.55rem;
  color: var(--primary-hover);
  background: var(--color-surface);
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
}

.proposal-cost {
  display: grid;
  gap: 0.2rem;
  margin-top: auto;
  padding-top: 0.8rem;
  color: var(--primary-hover);
  font-size: 0.82rem;
  font-weight: 700;
}

.result-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
  padding-top: 1.5rem;
}

.result-actions-top {
  justify-content: flex-start;
  padding-top: 0;
  padding-bottom: 1.25rem;
}

.keep-candidates-toggle {
  display: inline-flex;
  gap: 0.55rem;
  align-items: center;
  margin-top: 0.5rem;
  color: var(--color-text);
  font-weight: 700;
}

.keep-candidates-toggle input {
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
}

.result-primary-button {
  width: auto;
  min-width: 320px;
}

.flow-selection-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.5rem 0;
  padding: 0.9rem 1rem;
  background: var(--color-primary-light);
  border-radius: 10px;
}

.flow-selection-banner span {
  color: var(--color-text-muted);
  font-size: 0.78rem;
}

.regeneration-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0 1.5rem;
  padding: 1rem;
  background: #fffaf0;
  border: 1px solid #ead9ad;
  border-radius: 12px;
}

.regeneration-toolbar p {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
}

.regeneration-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem;
}

.regeneration-actions > span {
  white-space: nowrap;
  color: var(--color-text-muted);
  font-size: 0.78rem;
  font-weight: 700;
}

.regenerate-button {
  color: var(--primary-hover);
  border-color: var(--primary-color);
}

.regenerate-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.daily-choice-day {
  margin-top: 1.25rem;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 14px;
}

.daily-choice-day > h3 {
  margin: 0;
  padding: 0.9rem 1.1rem;
  background: var(--color-cool-bg);
  font-size: 1.05rem;
}

.daily-choice-group {
  padding: 1.2rem;
  border-top: 1px solid var(--color-border-light);
}

.day-schedule-preview {
  padding: 1.2rem;
  background: #fbfcfa;
  border-top: 1px solid var(--color-border-light);
}

.schedule-preview-item {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 1rem;
  align-items: start;
  padding: 0.85rem 0;
  border-top: 1px solid var(--color-border-light);
}

.schedule-preview-item:first-of-type {
  border-top: 0;
}

.daily-choice-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  margin-bottom: 0.8rem;
}

.daily-choice-heading > div {
  display: flex;
  align-items: baseline;
  gap: 0.65rem;
}

.daily-choice-heading span {
  color: var(--color-text-light);
  font-size: 0.75rem;
}

.regeneration-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.65rem;
  color: var(--primary-hover);
  background: var(--color-surface);
  border: 1px solid var(--primary-color);
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
}

.daily-proposal-card {
  min-height: 220px;
}

.google-maps-link {
  display: inline-flex;
  margin-top: 0.6rem;
  color: var(--primary-hover);
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: underline;
}

.selected-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
  margin: 1.5rem 0;
}

.selected-summary div {
  display: flex;
  flex-direction: column;
  padding: 0.9rem;
  background: var(--color-primary-light);
  border-radius: 10px;
}

.selected-summary span {
  color: var(--color-text-muted);
  font-size: 0.72rem;
}

.itinerary-days {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.itinerary-day {
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 14px;
}

.itinerary-day h3 {
  margin: 0;
  padding: 0.85rem 1rem;
  background: var(--color-cool-bg);
  font-size: 1rem;
}

.itinerary-item {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 1rem;
  align-items: start;
  padding: 1rem;
  border-top: 1px solid var(--color-border-light);
}

.itinerary-time {
  color: var(--primary-hover);
  font-size: 0.85rem;
  font-weight: 800;
}

.itinerary-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
}

.itinerary-content p {
  margin: 0;
  font-size: 0.8rem;
}

.category-pill {
  padding: 0.15rem 0.5rem;
  color: var(--primary-hover);
  background: var(--color-primary-light);
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
}

.itinerary-cost {
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.approval-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1.2rem;
  background: #f4f9f0;
  border: 1px solid #cfe1c2;
  border-radius: 14px;
}

.approval-box p {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
}

.approval-button {
  width: auto;
  min-width: 230px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .consult-grid {
    grid-template-columns: 1fr;
  }

  .submit-panel {
    position: static;
  }

  .proposal-grid {
    grid-template-columns: 1fr;
  }

  .regeneration-toolbar,
  .regeneration-actions {
    align-items: stretch;
    flex-direction: column;
  }
}

@media (max-width: 640px) {
  .consult-shell {
    width: min(100% - 1rem, 1180px);
    padding-top: 1rem;
  }

  .hero {
    padding: 1.4rem;
  }

  .hero-icon {
    display: none;
  }

  .consult-progress {
    overflow-x: auto;
  }

  .consult-progress ol {
    min-width: 620px;
  }

  .form-section {
    padding: 1.25rem;
  }

  .two-columns,
  .choice-row {
    grid-template-columns: 1fr 1fr;
  }

  .result-panel {
    padding: 1.2rem;
  }

  .result-heading,
  .approval-box {
    align-items: stretch;
    flex-direction: column;
  }

  .selected-summary {
    grid-template-columns: 1fr;
  }

  .flow-selection-banner,
  .daily-choice-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.25rem;
  }

  .daily-choice-heading > div {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.2rem;
  }

  .daily-choice-group {
    padding: 0.9rem;
  }

  .itinerary-item {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }

  .schedule-preview-item {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }

  .result-primary-button,
  .approval-button {
    width: 100%;
    min-width: 0;
  }
}
</style>
